import React, { useEffect, useState } from "react";
import axiosInstance from "../axiosConfig";
import ContributionUpdate from "./ContributionUpdate";
import './ContributionPage.css';;

const getCurrentQuarter = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const q = month <= 2 ? 'Q1' : month <= 5 ? 'Q2' : month <= 8 ? 'Q3' : 'Q4';
  return `${year}-${q}`;
};

const shiftQuarter = (quarter, direction) => {
  const year = parseInt(quarter.slice(0, 4));
  const q = parseInt(quarter.slice(-1));
  const newQ = q + direction;
  if (newQ > 4) return `${year + 1}-Q1`;
  if (newQ < 1) return `${year - 1}-Q4`;
  return `${year}-Q${newQ}`;
};

const getQuarterDateRange = () => {
  const now = new Date();
  const month = now.getMonth();
  if (month <= 2) return 'Jan-Mar';
  if (month <= 5) return 'Apr-Jun';
  if (month <= 8) return 'Jul-Sep';
  return 'Oct-Dec';
};

const getPrizeDeliveryDate = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  if (month <= 2) return `April 15th, ${year}`;
  if (month <= 5) return `July 15th, ${year}`;
  if (month <= 8) return `October 15th, ${year}`;
  return `January 15th, ${year + 1}`;
};

const ContributionsPage = () => {
  const [userContributions, setUserContributions] = useState([]);
  const [userPosts, setUserPosts] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [activeTab, setActiveTab] = useState('updates');
  const [loading, setLoading] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [isOverlayVisible, setIsOverlayVisible] = useState(false);

  useEffect(() => {
    fetchAll();
  }, [selectedQuarter]);

  const fetchAll = async () => {
    setLoading(true);
    await Promise.all([fetchUserContributions(), fetchUserPosts(), fetchLeaderboard()]);
    setLoading(false);
  };

  const fetchUserContributions = async () => {
    try {
      const response = await axiosInstance.get("/user_contributions/");
      const all = response.data.contributions || [];

      // Filter to selected quarter
      const year = parseInt(selectedQuarter.slice(0, 4));
      const q = parseInt(selectedQuarter.slice(-1));
      const qStart = new Date(year, (q - 1) * 3, 1);
      const qEnd = q < 4 ? new Date(year, q * 3, 1) : new Date(year + 1, 0, 1);
      const filtered = all.filter(c => {
        const d = new Date(c.submitted_at);
        return d >= qStart && d < qEnd;
      });

      setUserContributions(filtered);
    } catch (error) {
      console.error("Error fetching user contributions:", error);
    }
  };

  const fetchUserPosts = async () => {
    try {
      const response = await axiosInstance.get(`/user_post_points/?quarter=${selectedQuarter}`);
      setUserPosts(response.data.posts || []);
    } catch (error) {
      console.error("Error fetching user posts:", error);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const response = await axiosInstance.get(`/leaderboard_by_quarter/?quarter=${selectedQuarter}`);
      setLeaderboard(response.data.leaderboard || []);
    } catch (error) {
      console.error("Error fetching leaderboard:", error);
    }
  };

  const canGoBack = shiftQuarter(selectedQuarter, -1) >= '2025-Q2';
  const canGoForward = shiftQuarter(selectedQuarter, 1) <= getCurrentQuarter();

  return (
    <div className="lb-page">
      {/* Header */}
      <div className="lb-header">
        <div className="lb-header-top">
          <h1 className="lb-title">Data Revisions</h1>
          <button className="uf-create-btn" onClick={() => setShowUpdateForm(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            Submit Update
          </button>
        </div>
        <div className="lb-prize-banner" onClick={() => setIsOverlayVisible(true)}>
          <svg className="lb-prize-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#2c5c4f" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" /><path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" /><path d="M4 22h16" /><path d="M10 14.66V17c0 .55-.47.98-.97 1.21C7.85 18.75 7 20 7 22" /><path d="M14 14.66V17c0 .55.47.98.97 1.21C16.15 18.75 17 20 17 22" /><path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
          </svg>
          <span className="lb-prize-text">
            {getCurrentQuarter()} Prize Pool: <strong>$1,000</strong>
          </span>
          <span className="lb-prize-arrow">›</span>
        </div>
      </div>

      {/* Leaderboard */}
      <div className="lb-section">
        <div className="lb-section-header">
          <h2 className="lb-section-title">Leaderboard</h2>
          <div className="lb-quarter-nav">
            <button
              className="lb-quarter-arrow"
              onClick={() => canGoBack && setSelectedQuarter(shiftQuarter(selectedQuarter, -1))}
              disabled={!canGoBack}
            >‹</button>
            <span className="lb-quarter-text">{selectedQuarter}</span>
            <button
              className="lb-quarter-arrow"
              onClick={() => canGoForward && setSelectedQuarter(shiftQuarter(selectedQuarter, 1))}
              disabled={!canGoForward}
            >›</button>
          </div>
        </div>

        <div className="lb-leaderboard-card">
          {Array.from({ length: 10 }, (_, index) => {
            const user = leaderboard[index];
            return (
              <div key={index} className={`lb-row ${index === 9 ? 'lb-row-last' : ''}`}>
                <span className="lb-rank">{index + 1}</span>
                {user ? (
                  <>
                    <span className="lb-username">{user.username}</span>
                    <span className="lb-points">{user.total_points} pts</span>
                  </>
                ) : (
                  <span className="lb-username lb-empty">—</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Your Activity — Tabbed Card */}
      <div className="lb-activity-card">
        <div className="lb-tab-bar">
          <button
            className={`lb-tab ${activeTab === 'updates' ? 'active' : ''}`}
            onClick={() => setActiveTab('updates')}
          >Your Updates</button>
          <button
            className={`lb-tab ${activeTab === 'posts' ? 'active' : ''}`}
            onClick={() => setActiveTab('posts')}
          >Your Posts</button>
        </div>

        <div className="lb-tab-content">
          {activeTab === 'updates' ? (
            userContributions.length > 0 ? (
              <div className="lb-table-scroll">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>Category</th>
                      <th>Status</th>
                      <th>Points</th>
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userContributions.map((c) => (
                      <tr key={c.id}>
                        <td>{c.categories?.join(', ') || 'N/A'}</td>
                        <td>
                          <span className={`lb-status ${c.status?.toLowerCase()}`}>
                            {c.status}
                          </span>
                        </td>
                        <td>{c.points}</td>
                        <td>{new Date(c.submitted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="lb-empty-state">No updates this quarter</div>
            )
          ) : (
            userPosts.length > 0 ? (
              <div className="lb-table-scroll">
                <table className="lb-table">
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Type</th>
                      <th>Points</th>
                      <th>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPosts.map((p) => (
                      <tr key={`${p.type}-${p.id}`}>
                        <td>{p.title || 'Untitled'}</td>
                        <td>
                          <span className="lb-status post">{p.type === 'club' ? 'Club' : 'Forum'}</span>
                        </td>
                        <td>{p.points}</td>
                        <td>{new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="lb-empty-state">No posts this quarter</div>
            )
          )}
        </div>
      </div>

      {/* Prize Pool Modal */}
      {isOverlayVisible && (
        <div className="npm-overlay" onClick={() => setIsOverlayVisible(false)}>
          <div className="npm-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 440 }}>
            <div className="npm-header">
              <h2 className="npm-title">Official Contest Rules</h2>
              <button className="npm-close" onClick={() => setIsOverlayVisible(false)}>×</button>
            </div>
            <div style={{ padding: '0 20px 20px' }}>
              <h4 style={{ color: '#2c5c4f', marginBottom: 6 }}>Prize</h4>
              <p style={{ fontSize: 14, color: '#333', marginBottom: 16, lineHeight: 1.5 }}>
                The top 10 contributors for {selectedQuarter} ({getQuarterDateRange()}) will each receive a $100 digital gift card, delivered via email by {getPrizeDeliveryDate()}.
              </p>
              <h4 style={{ color: '#2c5c4f', marginBottom: 6 }}>Point System</h4>
              <ul style={{ fontSize: 14, color: '#333', marginBottom: 16, paddingLeft: 20, lineHeight: 1.6 }}>
                <li>Missing Club submissions: 10 points</li>
                <li>All other data corrections: 5 points</li>
                <li>Forum or club post: 5 points</li>
              </ul>
              <h4 style={{ color: '#2c5c4f', marginBottom: 6 }}>How to Enter</h4>
              <p style={{ fontSize: 14, color: '#333', marginBottom: 16, lineHeight: 1.5 }}>
                Submit data corrections, missing club information, or create posts in club discussions and forums. Data contributions are reviewed by our research team. Post points are awarded automatically.
              </p>
              <h4 style={{ color: '#2c5c4f', marginBottom: 6 }}>Winner Selection</h4>
              <p style={{ fontSize: 14, color: '#333', lineHeight: 1.5 }}>
                Winners are determined by the top 10 highest point totals at the end of each quarter. This is not a random drawing.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Submit Update Modal */}
      {showUpdateForm && (
        <ContributionUpdate
          courseId={null}
          onClose={() => setShowUpdateForm(false)}
        />
      )}
    </div>
  );
};

export default ContributionsPage;
